/**
 * Inline replacement for TanStack Query's `useMutation`.
 *
 * Mirrors the surface this app uses:
 *   - mutationFn(variables) => Promise<data>
 *   - hook-level onSuccess / onError / onSettled, with (data, variables) args
 *   - mutate(variables, callbacks?) — fire-and-forget, never rejects
 *   - mutateAsync(variables, callbacks?) — returns the promise, rejects on error
 *   - isPending / isError / isSuccess / data / error / reset
 *
 * Callback order matches TanStack: hook-level callback first, then the
 * per-call callback passed to mutate/mutateAsync.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface MutationCallbacks<TData, TVariables, TError = unknown> {
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: TError, variables: TVariables) => void;
  onSettled?: (data: TData | undefined, error: TError | null, variables: TVariables) => void;
}

export interface UseMutationOptions<TData, TVariables, TError = unknown>
  extends MutationCallbacks<TData, TVariables, TError> {
  mutationFn: (variables: TVariables) => Promise<TData>;
}

export interface UseMutationResult<TData, TVariables, TError = unknown> {
  mutate: (variables: TVariables, callbacks?: MutationCallbacks<TData, TVariables, TError>) => void;
  mutateAsync: (
    variables: TVariables,
    callbacks?: MutationCallbacks<TData, TVariables, TError>,
  ) => Promise<TData>;
  data: TData | undefined;
  error: TError | null;
  status: 'idle' | 'pending' | 'success' | 'error';
  isIdle: boolean;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  reset: () => void;
}

export function useMutation<TData = unknown, TVariables = void, TError = unknown>(
  options: UseMutationOptions<TData, TVariables, TError>,
): UseMutationResult<TData, TVariables, TError> {
  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [data, setData] = useState<TData | undefined>(undefined);
  const [error, setError] = useState<TError | null>(null);

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const mutateAsync = useCallback(
    async (
      variables: TVariables,
      callbacks?: MutationCallbacks<TData, TVariables, TError>,
    ): Promise<TData> => {
      if (mountedRef.current) {
        setStatus('pending');
        setError(null);
      }
      try {
        const result = await optionsRef.current.mutationFn(variables);
        if (mountedRef.current) {
          setData(result);
          setStatus('success');
        }
        optionsRef.current.onSuccess?.(result, variables);
        callbacks?.onSuccess?.(result, variables);
        optionsRef.current.onSettled?.(result, null, variables);
        callbacks?.onSettled?.(result, null, variables);
        return result;
      } catch (err) {
        const typedErr = err as TError;
        if (mountedRef.current) {
          setError(typedErr);
          setStatus('error');
        }
        optionsRef.current.onError?.(typedErr, variables);
        callbacks?.onError?.(typedErr, variables);
        optionsRef.current.onSettled?.(undefined, typedErr, variables);
        callbacks?.onSettled?.(undefined, typedErr, variables);
        throw err;
      }
    },
    [],
  );

  const mutate = useCallback(
    (variables: TVariables, callbacks?: MutationCallbacks<TData, TVariables, TError>): void => {
      // Fire-and-forget: errors are surfaced via onError + state, not a rejection.
      void mutateAsync(variables, callbacks).catch(() => {});
    },
    [mutateAsync],
  );

  const reset = useCallback(() => {
    if (!mountedRef.current) return;
    setStatus('idle');
    setData(undefined);
    setError(null);
  }, []);

  return {
    mutate,
    mutateAsync,
    data,
    error,
    status,
    isIdle: status === 'idle',
    isPending: status === 'pending',
    isError: status === 'error',
    isSuccess: status === 'success',
    reset,
  };
}
